'use client';

import React, { useState } from 'react';
import { GlobalButton } from './GlobalButton';
import GlobalSidebar from './GlobalSidebar';
import { GlobalCheckbox } from './GlobalCheckbox';
import { useToast } from '../context/ToastProvider';
import { useTheme } from '../context/ThemeProvider';
import { useLocale } from '../context/DynamicLocaleProvider';
import { useUser } from '../context/UserContext';
import LocalLLMTrust from './LocalLLMTrust';
import styles from './EntityGenerationSidebar.module.css';
import chipStyles from './GlobalEntityAutocomplete.module.css';
import type { RecordType } from '../types';

interface EntityGenerationSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  selectedEntities: Array<{
    id: string;
    type: RecordType;
    title: string;
  }>;
  onSuccess?: () => void;
}

export default function EntityGenerationSidebar({
  isOpen,
  onClose,
  projectId,
  selectedEntities,
  onSuccess
}: EntityGenerationSidebarProps) {
  const { t } = useLocale();
  const showToast = useToast();
  const { theme } = useTheme();
  const { user } = useUser();
  const isDarkMode = theme === 'dark';

  const [selectedOutputTypes, setSelectedOutputTypes] = useState<RecordType[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Determine input entity type from selected entities (should all be the same type)
  const inputEntityType = selectedEntities.length > 0 ? selectedEntities[0].type : null;
  const inputEntityIds = selectedEntities.map(e => e.id);

  // Available output types based on input type
  const getAvailableOutputTypes = (): RecordType[] => {
    if (!inputEntityType) return [];
    
    // URS can generate: FS, RISK, TEST, SOP
    if (inputEntityType === 'URS') {
      return ['FS', 'RISK', 'TEST', 'SOP'];
    }
    // FS can generate: RISK, TEST, SOP
    if (inputEntityType === 'FS') {
      return ['RISK', 'TEST', 'SOP'];
    }
    // RISK can generate: TEST, SOP
    if (inputEntityType === 'RISK') {
      return ['TEST', 'SOP'];
    }
    // TEST can generate: SOP
    if (inputEntityType === 'TEST') {
      return ['SOP'];
    }
    // SOP cannot generate anything
    return [];
  };

  const availableOutputTypes = getAvailableOutputTypes();

  // Get localized entity type names
  const getEntityTypeName = (type: RecordType): string => {
    const nameMap: Record<RecordType, string> = {
      'URS': t('project_page_tab_urs') || 'Requirements',
      'FS': t('project_page_tab_fs') || 'Specifications',
      'RISK': t('project_page_tab_ra') || 'Risks',
      'TEST': t('project_page_tab_tests') || 'Tests',
      'SOP': t('project_page_tab_sops') || 'SOPs',
    };
    return nameMap[type] || type;
  };

  // Get entity type abbreviation
  const getEntityTypeAbbr = (type: RecordType): string => {
    return type;
  };

  const handleOutputTypeToggle = (type: RecordType) => {
    setSelectedOutputTypes(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  const handleFire = async () => {
    if (!inputEntityType || selectedOutputTypes.length === 0) {
      showToast(
        t('entity_generation_no_output_selected') || 'Please select at least one output entity type',
        'error'
      );
      return;
    }

    if (!user?.uid) {
      showToast(
        t('entity_generation_user_not_authenticated') || 'User not authenticated',
        'error'
      );
      return;
    }

    setIsLoading(true);
    showToast(
      t('entity_generation_starting') || 'Starting entity generation...',
      'info'
    );

    try {
      // Construct the Cloud Run URL for the function
      // For v2 functions in europe-west3, the URL pattern is:
      // https://[function-name]-[hash]-[region].a.run.app
      // The hash is project-specific and may need to be updated after deployment
      const functionUrl = process.env.NEXT_PUBLIC_COMPLETE_ENTITY_COMPLIANCE_URL || 
        'https://completeentitycompliance-3l53kqimxq-ey.a.run.app';
      
      // Alternative: if the function is deployed via HTTP trigger, use this pattern:
      // const functionUrl = `https://europe-west3-thescon.cloudfunctions.net/completeEntityCompliance`;

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          projectId,
          inputEntityType,
          inputEntityIds,
          outputEntityTypes: selectedOutputTypes,
          userId: user.uid,
          language: 'en', // TODO: Get from user preferences or locale context
        }),
      });

      if (!response.ok) {
        let errorData: any = {};
        try {
          errorData = await response.json();
        } catch (e) {
          const text = await response.text();
          throw new Error(`HTTP error! status: ${response.status}, body: ${text}`);
        }
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        const entityCounts = Object.entries(result.generatedEntities || {})
          .map(([type, entities]: [string, any]) => `${type}: ${Array.isArray(entities) ? entities.length : 0}`)
          .join(', ');

        showToast(
          t('entity_generation_success') || `Successfully generated entities: ${entityCounts}`,
          'success'
        );

        // Reset form
        setSelectedOutputTypes([]);

        // Call success callback
        if (onSuccess) {
          onSuccess();
        }

        // Close sidebar after a short delay
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        throw new Error(result.error || 'Unknown error occurred');
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Entity generation error:', error);
      showToast(
        t('entity_generation_error') || `Failed to generate entities: ${errorMessage}`,
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <GlobalSidebar isOpen={isOpen} onClose={onClose}>
      <div className={styles.content}>
          {/* Header */}
          <div className={styles.header}>
            <img 
              src={isDarkMode ? "/assets/identity/logo-dark.svg" : "/assets/identity/logo.svg"} 
              alt="Thescon" 
              width={16} 
              height={16} 
            />
            <span>|</span>
            <h2>AI</h2>
          </div>

          {/* Selected Entities Section */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
              {t('entity_generation_selected_entities') || 'Selected Entities'} ({selectedEntities.length})
            </h3>
            {selectedEntities.length === 0 ? (
              <p className={styles.emptyMessage}>
                {t('entity_generation_no_entities_selected') || 'No entities selected'}
              </p>
            ) : (
              <div className={chipStyles.chips}>
                {selectedEntities.slice(0, 10).map((entity) => (
                  <span key={entity.id} className={chipStyles.chip}>
                    <span className={chipStyles.chipId}>{entity.id}</span>
                    {entity.title && (
                      <span className={chipStyles.chipLabel}>{entity.title}</span>
                    )}
                  </span>
                ))}
                {selectedEntities.length > 10 && (
                  <span className={styles.moreText}>
                    {t('entity_generation_and_more')?.replace('{count}', String(selectedEntities.length - 10)) || `and ${selectedEntities.length - 10} more...`}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Output Types Section */}
          {inputEntityType && availableOutputTypes.length > 0 ? (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>
                {t('entity_generation_output_types') || 'Generate Output Entities'}
              </h3>
              <p className={styles.sectionDescription}>
                {t('entity_generation_select_output_types') || 
                  `Select which entity types to generate from ${inputEntityType}:`}
              </p>
              <div className={styles.checkboxGroup}>
                {availableOutputTypes.map((type) => (
                  <GlobalCheckbox
                    key={type}
                    checked={selectedOutputTypes.includes(type)}
                    onChange={() => handleOutputTypeToggle(type)}
                    disabled={isLoading}
                    label={`${getEntityTypeName(type)} (${getEntityTypeAbbr(type)})`}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className={styles.section}>
              <p className={styles.emptyMessage}>
                {t('entity_generation_no_output_available') || 
                  `Cannot generate entities from ${inputEntityType || 'selected'} type`}
              </p>
            </div>
          )}

          {/* Fire Button */}
          <div className={styles.actionSection}>
            <GlobalButton
              onClick={handleFire}
              disabled={isLoading || selectedOutputTypes.length === 0 || selectedEntities.length === 0}
              isLoading={isLoading}
            >
              {isLoading 
                ? (t('entity_generation_generating') || 'Generating...')
                : (t('entity_generation_fire') || 'Fire')}
            </GlobalButton>
          </div>

          <LocalLLMTrust />
      </div>
    </GlobalSidebar>
  );
}

